import { ReactNode } from "react";
import { useLocation, useNavigationType, useSearchParams } from "react-router-dom";
import { useCurrentOrderSummary } from "@app/data";
import { matchTrackingRoute } from "@app/shared/routing/matchTrackingRoute";
import { useIsDesktopViewport } from "@app/shared/hooks/useIsDesktopViewport";
import { MapView, MapProviderConfig } from "@app/features/home/components/Map";
import { cx } from "@app/shared/utils/cx";
import styles from "./DesktopTrackingShell.module.css";

export type DesktopTrackingShellProps = { children: ReactNode };

/**
 * Desktop-only two-pane frame: a fixed, phone-width content panel on the
 * left (whatever `<Routes>` currently renders) beside a permanently-mounted
 * map on the right. Wraps `<Routes>` from *outside* the router tree on
 * purpose — `/:trackCode` (Home) and `/details/:trackCode/*` are separate
 * top-level routes with no shared parent, so a layout-route/`<Outlet>`
 * couldn't keep the map mounted across navigation between them the way a
 * plain wrapping component can. Below `breakpoints.xl` (mobile/tablet) this
 * renders `children` completely unwrapped — zero DOM/behavior change there.
 *
 * The panel is styled to match mobile as closely as possible (same cards,
 * spacing, grouping — just alongside the map), so it carries no radius,
 * shadow, or padding of its own. The plain `"desktop-tracking-panel"`
 * marker class on `.panel` exists so `Card.module.css` can re-assert the
 * mobile grouped-list look for its own `min-width: 768px` reversion, which
 * would otherwise fire here too — a real desktop window is always >=768px
 * even though this panel always renders at mobile width. See that file.
 */
export const DesktopTrackingShell = ({ children }: DesktopTrackingShellProps) => {
  const isDesktop = useIsDesktopViewport();
  const location = useLocation();
  const navigationType = useNavigationType();
  const [searchParams] = useSearchParams();

  const routeInfo = matchTrackingRoute(location.pathname);
  const order = useCurrentOrderSummary(routeInfo?.trackCode ?? "", routeInfo?.isDropoffTracking ?? false);

  if (!isDesktop || !routeInfo) {
    return <>{children}</>;
  }

  // Pickup has no id of its own in the URL — resolve it from the order
  // summary, same as `StopDetailsPage` does. `null` outside a Stop Details
  // page, which leaves the map's usual fit-on-load behavior untouched.
  const focusMarkerId = !routeInfo.focusedStop
    ? null
    : routeInfo.focusedStop.kind === "pickup"
      ? order?.pickup?.id ?? null
      : routeInfo.focusedStop.stopId;

  const mapParam = searchParams.get("map");
  const providerOverride: MapProviderConfig["kind"] | undefined =
    mapParam === "osm" || mapParam === "google" ? mapParam : undefined;

  // POP = back/forward navigation (browser buttons, `navigate(-1)`) — slide
  // the incoming panel in from the left, mirroring where it came from;
  // everything else (PUSH/REPLACE, i.e. moving deeper) slides in from the
  // right, like a native drawer push.
  const direction = navigationType === "POP" ? "back" : "forward";

  return (
    <div className={styles.shell}>
      <div className={cx(styles.panel, "desktop-tracking-panel")}>
        <div className={styles.transitionViewport}>
          <div
            key={location.pathname}
            className={cx(styles.slide, direction === "back" ? styles.slideBack : styles.slideForward)}
          >
            {children}
          </div>
        </div>
      </div>
      <div className={styles.mapPane}>
        {order ? (
          <MapView
            order={order}
            isDropoffTracking={routeInfo.isDropoffTracking}
            focusMarkerId={focusMarkerId}
            providerOverride={providerOverride}
            className={styles.map}
          />
        ) : null}
      </div>
    </div>
  );
};
